import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Brain, AudioWaveform } from "lucide-react";

function TiltCard({ children, testId }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rx = useSpring(useTransform(y, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(x, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 14 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left) / r.width - 0.5);
    y.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { x.set(0); y.set(0); };

  return (
    <motion.div
      ref={ref}
      data-testid={testId}
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="relative bg-[#FBF9F5] border border-[#121110]/12 p-9 lg:p-11 will-change-transform"
    >
      <div style={{ transform: "translateZ(40px)" }}>{children}</div>
    </motion.div>
  );
}

export default function Fusion() {
  return (
    <section
      id="fusion"
      data-testid="fusion-section"
      className="px-6 lg:px-14 py-28 lg:py-40 bg-[#F3EFEA] border-b border-[#121110]/10"
    >
      <p className="font-mono-accent text-[11px] uppercase tracking-[0.4em] text-[#8C857B] mb-10">
        03 / The Approach
      </p>

      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="font-serif-display font-light tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-[#121110] max-w-[18ch]"
      >
        Where <span className="italic text-[#8C857B]">psychology</span> meets composition.
      </motion.h2>

      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.2 }}
        className="mt-8 max-w-2xl text-[#5C5650] text-base sm:text-lg leading-relaxed font-light"
      >
        I am a psychologist and a creative. Through a blend of musical craftsmanship, strategic
        thinking and psychological insight, I decode the DNA of a brand and translate it into sonic
        worlds that communicate with nuance, evoke emotion and leave a lasting impression.
      </motion.p>

      <div className="mt-16 grid md:grid-cols-2 gap-8 lg:gap-10">
        <TiltCard testId="fusion-card-psychology">
          <Brain className="h-8 w-8 text-[#C5A880] mb-8" strokeWidth={1.1} />
          <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#8C857B]">
            The Mind
          </span>
          <h3 className="font-serif-display font-light text-3xl lg:text-4xl text-[#121110] mt-3 mb-4">
            Psychological insight
          </h3>
          <p className="text-[#5C5650] text-sm sm:text-base leading-relaxed font-light">
            Grounded in psychoacoustics and emotional cognition — understanding why certain sounds
            evoke trust, desire, calm or prestige, and how memory binds a brand to a feeling.
          </p>
        </TiltCard>

        <TiltCard testId="fusion-card-music">
          <AudioWaveform className="h-8 w-8 text-[#C5A880] mb-8" strokeWidth={1.1} />
          <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#8C857B]">
            The Craft
          </span>
          <h3 className="font-serif-display font-light text-3xl lg:text-4xl text-[#121110] mt-3 mb-4">
            Musical craftsmanship
          </h3>
          <p className="text-[#5C5650] text-sm sm:text-base leading-relaxed font-light">
            Composition, harmony and sound design refined over years — translating strategy and
            emotion into meticulously crafted identities worthy of the world's finest houses.
          </p>
        </TiltCard>
      </div>
    </section>
  );
}
